import * as React from 'react'
import { cn } from '@/lib/utils'

/**
 * PageHeader - Standardized page header component
 * 
 * Provides consistent title, description, and action area styling.
 * Use this for all page headers to maintain visual consistency.
 * 
 * @example
 * ```tsx
 * <PageHeader
 *   title="Analytics Dashboard"
 *   description="View your site traffic and engagement metrics"
 *   actions={<Button>Export</Button>}
 * />
 * ```
 *
 * `actions` accepts any ReactNode, but for pages with a Filter · Date · Refresh
 * cluster, keep every control at a consistent `h-8` with `gap-2` between them
 * so the row reads as one unit:
 *
 * @example
 * ```tsx
 * <PageHeader
 *   title="Traffic"
 *   actions={
 *     <div className="flex items-center gap-2">
 *       <Button variant="outline" size="sm" className="h-8">Filter</Button>
 *       <DateDropdown className="h-8" />
 *       <Button variant="outline" size="icon" className="h-8 w-8"><RefreshIcon /></Button>
 *     </div>
 *   }
 * />
 * ```
 */
interface PageHeaderProps {
  /** Main page title */
  title: string
  /** Optional description text below title */
  description?: string
  /** Optional actions (buttons, etc.) to display on the right */
  actions?: React.ReactNode
  /** Optional breadcrumb navigation */
  breadcrumb?: React.ReactNode
  /** Custom className */
  className?: string
}

export function PageHeader({
  title,
  description,
  actions,
  breadcrumb,
  className,
}: PageHeaderProps) {
  // Paper "App v3": title 22/28 semibold at -0.02em, subtitle 13/16 at 62% ink,
  // 6px apart; actions bottom-aligned with the subtitle; 24px to the content.
  return (
    <div className={cn('flex flex-col gap-3 mb-6', className)}>
      {breadcrumb && (
        <div className="text-[13px] font-medium leading-4 text-foreground/60">
          {breadcrumb}
        </div>
      )}
      <div className="flex flex-wrap items-end justify-between gap-x-4 gap-y-3">
        <div className="flex min-w-0 flex-col gap-1.5">
          <h1 className="text-[22px] font-semibold leading-7 tracking-[-0.02em] text-foreground">
            {title}
          </h1>
          {description && (
            <p className="text-[13px] leading-4 text-foreground/[0.62] max-w-2xl">
              {description}
            </p>
          )}
        </div>
        {actions && (
          <div className="flex items-center gap-2 shrink-0">
            {actions}
          </div>
        )}
      </div>
    </div>
  )
}
